import React, { useCallback, useEffect, useState } from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  useClipboard,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/core';
import { RiFileCopyLine, RiCheckboxMultipleFill, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from '../../../../models/hooks';
import {
  clearErrorMessage,
  errorFormat,
  FormattedError,
  getErrorMessage,
  hasErrorMessage,
} from '../../../../utils/FormErrorUtils';
import { I9FormData } from '../I9Types';
import colors from '../../../../styles/colors';

export type I9KeysAndUrlProps = {
  accountId: string | undefined;
};

interface WorkBrightCredentials {
  apiKey: string;
  subDomain: string;
  username: string;
  password: string;
}

interface WorkBrightResponse {
  data?: {
    message?: string;
  };
}
export const I9KeysAndUrl = ({ accountId }: I9KeysAndUrlProps): JSX.Element => {
  const toast = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showApiKey, setShowApiKey] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [hasI9Subscribed, setHasI9Subscribed] = React.useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [i9FormData, setI9FormData] = React.useState({
    apiKey: '',
    subDomain: '',
    username: '',
    password: '',
    webhookApi: '',
  } as I9FormData);

  const { hasCopied, onCopy } = useClipboard(i9FormData.webhookApi || '');

  const { getWorkBrightCredentials, saveWorkBrightCredentials } = useStoreActions((actions) => actions.administration);

  const handleChange = (value: string, fieldName: string) => {
    setI9FormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
    setFormErrors(clearErrorMessage(formErrors, fieldName));
  };

  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      apiKey: Joi.string()
        .required()
        .messages({
          'string.empty': t('i9Verification:apiKeyRequired'),
        }),
      subDomain: Joi.string()
        .required()
        .messages({
          'string.empty': t('i9Verification:subDomainRequired'),
        }),
      username: Joi.string()
        .required()
        .messages({
          'string.empty': t('i9Verification:usernameRequired'),
        }),
      password: Joi.string()
        .required()
        .messages({ 'string.empty': t('i9Verification:passwordRequired') }),
      webhookApi: Joi.string().allow('').allow(null).optional(),
    });

  const formValidation = (formData: I9FormData) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { error } = schema.validate(formData);

    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }

    setFormErrors([]);
    return true;
  };

  const getWorkBrightCredential = useCallback(async () => {
    if (accountId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const getWorkBrightCredentialResponse: WorkBrightCredentials = await getWorkBrightCredentials({ accountId });
      setI9FormData(getWorkBrightCredentialResponse);
      if (getWorkBrightCredentialResponse.apiKey) {
        setHasI9Subscribed(true);
      }
    }
  }, [accountId, getWorkBrightCredentials]);

  const submitWorkBrightCredentials = async () => {
    if (formValidation(i9FormData) && accountId) {
      try {
        setIsLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const workBrightResponse: WorkBrightResponse = await saveWorkBrightCredentials({ ...i9FormData, accountId });
        toast({
          title: workBrightResponse.data?.message ?? t('i9Verification:defaultSuccessMsg'),
          duration: 3000,
          isClosable: true,
        });
        await getWorkBrightCredential();
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getWorkBrightCredential();
  }, [getWorkBrightCredential]);

  return (
    <Box>
      <Box px={6} py={4}>
        <Heading as="h3" fontSize="md" lineHeight="1.3" mb={1}>
          {t('i9Verification:keysUrlLabel')}
        </Heading>
        <Text fontSize="xs" color="gray.500">
          {t('i9Verification:linkI9Label')}
        </Text>
      </Box>
      <Divider borderColor={colors.gray[50]} />
      <Box p={6}>
        <FormControl id="apiKey" mb={5} isInvalid={hasErrorMessage(formErrors, 'apiKey')} isRequired>
          <FormLabel fontSize="sm" fontWeight="700">
            {t('i9Verification:apiKeyLabel')}
          </FormLabel>
          <InputGroup size="md">
            <Input
              data-testid="ApiKeyInput"
              backgroundColor={colors.gray[150]}
              borderWidth={0}
              pr="4.5rem"
              type={showApiKey ? 'text' : 'password'}
              placeholder={t('i9Verification:apiKeyPlaceholder')}
              fontSize="sm"
              autoComplete="new-password"
              value={i9FormData.apiKey}
              onChange={(e) => handleChange(e.target.value, 'apiKey')}
            />
            <InputRightElement width="3.5rem">
              <Button
                h="1.75rem"
                size="xl"
                fontSize="xl"
                bg={colors.gray[150]}
                color="blue.500"
                pr={0}
                _hover={{ bg: colors.gray[150] }}
                _active={{ bg: colors.gray[150], outline: 'none' }}
                _focus={{ outline: 'none' }}
                data-testid="ToggleApiKeyVisibilityBtn"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? <RiEyeOffLine /> : <RiEyeLine />}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{getErrorMessage(formErrors, 'apiKey')}</FormErrorMessage>
        </FormControl>

        <FormControl id="subDomain" mb={5} isInvalid={hasErrorMessage(formErrors, 'subDomain')} isRequired>
          <FormLabel fontSize="sm" fontWeight="700">
            {t('i9Verification:subDomainLabel')}
          </FormLabel>
          <Input
            data-testid="SubDomainInput"
            backgroundColor={colors.gray[150]}
            borderWidth={0}
            pr="4.5rem"
            type="text"
            placeholder={t('i9Verification:subDomainPlaceholder')}
            fontSize="sm"
            value={i9FormData.subDomain}
            onChange={(e) => handleChange(e.target.value, 'subDomain')}
          />
          <FormErrorMessage>{getErrorMessage(formErrors, 'subDomain')}</FormErrorMessage>
        </FormControl>

        <FormControl id="username" mb={5} isInvalid={hasErrorMessage(formErrors, 'username')} isRequired>
          <FormLabel fontSize="sm" fontWeight="700">
            {t('i9Verification:usernameLabel')}
          </FormLabel>
          <Input
            data-testid="UserNameInput"
            backgroundColor={colors.gray[150]}
            borderWidth={0}
            pr="4.5rem"
            type="text"
            placeholder={t('i9Verification:usernamePlaceholder')}
            fontSize="sm"
            autoComplete="new-username"
            value={i9FormData.username}
            onChange={(e) => handleChange(e.target.value, 'username')}
          />
          <FormErrorMessage>{getErrorMessage(formErrors, 'username')}</FormErrorMessage>
        </FormControl>

        <FormControl id="password" mb={5} isInvalid={hasErrorMessage(formErrors, 'password')} isRequired>
          <FormLabel fontSize="sm" fontWeight="700">
            {t('i9Verification:passwordLabel')}
          </FormLabel>
          <InputGroup size="md">
            <Input
              data-testid="PasswordInput"
              backgroundColor={colors.gray[150]}
              borderWidth={0}
              pr="4.5rem"
              type={showPassword ? 'text' : 'password'}
              placeholder={t('i9Verification:passwordPlaceholder')}
              fontSize="sm"
              autoComplete="new-password"
              value={i9FormData.password}
              onChange={(e) => handleChange(e.target.value, 'password')}
            />
            <InputRightElement width="3.5rem">
              <Button
                h="1.75rem"
                size="xl"
                fontSize="xl"
                bg={colors.gray[150]}
                color="blue.500"
                pr={0}
                _hover={{ bg: colors.gray[150] }}
                _active={{ bg: colors.gray[150], outline: 'none' }}
                _focus={{ outline: 'none' }}
                data-testid="TogglePasswordVisibilityBtn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{getErrorMessage(formErrors, 'password')}</FormErrorMessage>
        </FormControl>

        {hasI9Subscribed && (
          <FormControl id="webhookUrl" mb={5}>
            <FormLabel fontSize="sm" fontWeight="700">
              {t('i9Verification:webhookUrlLabel')}
            </FormLabel>
            <Flex w="80%" alignItems="center">
              <Box flex={1}>
                <Text px={0} color="blue.500">
                  {`${i9FormData.webhookApi}`}
                </Text>
              </Box>
              <Box>
                <Button
                  onClick={onCopy}
                  ml={2}
                  fontSize="2xl"
                  bg={colors.gray[150]}
                  color="blue.500"
                  _hover={{ bg: colors.gray[150] }}
                  _active={{ bg: colors.gray[150] }}
                >
                  {/* istanbul ignore next */ hasCopied ? <RiCheckboxMultipleFill /> : <RiFileCopyLine />}
                </Button>
              </Box>
            </Flex>
          </FormControl>
        )}
        <Box textAlign="right" mb={10}>
          <Button
            isLoading={isLoading}
            data-testid="SubmitBtn"
            mt={4}
            colorScheme="blue"
            type="submit"
            onClick={() => submitWorkBrightCredentials()}
          >
            {t('i9Verification:submitBtn')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
